import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useFetch } from '@/modules/core/composables/useFetch'
import { Task, TaskList } from '../types'

export const useTaskListStore = defineStore('tasking/taskListStore', () => {
  const slug = ref<string | null>(null)
  const baseUrl = computed(() => (slug.value ? `/v4/${slug.value}/tasking` : null))

  //  loading

  const loadingIds = ref(new Set<number>())
  const isLoading = (id: number) => loadingIds.value.has(id)
  const setLoading = (id: number, loading: boolean) => {
    if (loading) {
      loadingIds.value.add(id)
    } else {
      loadingIds.value.delete(id)
    }
  }

  // state

  const taskList = ref<TaskList>()

  // queries

  const fetchTaskList = async (taskListId: number | string) => {
    if (!baseUrl.value) {
      throw new Error('Cannot fetch task list: slug is not set')
    }
    if (!taskListId) {
      throw new Error('Cannot fetch task list: invalid taskListId')
    }
    taskList.value = await useFetch(`${baseUrl.value}/task-lists/${taskListId}`).get<TaskList>()
  }

  // commands

  const setSlug = (newSlug: string) => {
    slug.value = newSlug
  }

  const completeTaskList = async (taskListId: number | string) => {
    const incompleteTasks = taskList.value?.tasks.filter((task) => !isTaskComplete(task))
    incompleteTasks.forEach((task) => setLoading(task.id, true))

    await useFetch(`${baseUrl.value}/task-lists/${taskListId}/complete`).post()
    await fetchTaskList(taskListId)

    incompleteTasks.forEach((task) => setLoading(task.id, false))
  }

  const hideTaskList = async (taskListId: number | string) => {
    try {
      taskList.value.is_visible = false
      await useFetch(`${baseUrl.value}/task-lists/${taskListId}/hide`).post()
    } finally {
      await fetchTaskList(taskListId)
    }
  }

  const reopenTaskList = async (taskListId: number | string) => {
    await useFetch(`${baseUrl.value}/task-lists/${taskListId}/reopen`).post()
    await fetchTaskList(taskListId)
  }

  const toggleOnHold = async (taskListId: number | string) => {
    const prev = taskList.value.on_hold
    try {
      taskList.value.on_hold = !prev
      await useFetch(`${baseUrl.value}/task-lists/${taskListId}/toggle-on-hold`).post()
    } finally {
      await fetchTaskList(taskListId)
    }
  }

  const showTaskList = async (taskListId: number | string) => {
    try {
      taskList.value.is_visible = true
      await useFetch(`${baseUrl.value}/task-lists/${taskListId}/show`).post()
    } finally {
      await fetchTaskList(taskListId)
    }
  }

  // Tasks

  const completeTask = async (taskId: number) => {
    try {
      setLoading(taskId, true)
      await useFetch(`${baseUrl.value}/tasks/${taskId}/complete`).post()
      completeTaskInUI(taskId)
    } catch (error) {
      await fetchTaskList(taskList.value?.id)
    } finally {
      setLoading(taskId, false)
    }
  }

  const reopenTask = async (taskId: number) => {
    try {
      setLoading(taskId, true)
      await useFetch(`${baseUrl.value}/tasks/${taskId}/reopen`).post()
      reopenTaskInUI(taskId)
    } catch (error) {
      await fetchTaskList(taskList.value?.id)
    } finally {
      setLoading(taskId, false)
    }
  }

  const completeTaskInUI = (taskId: number) => {
    const task = getTaskById(taskId)
    if (task) {
      // Parent task case
      task.status = 'complete'
      updateTaskCounts('increment')
    } else {
      // Child task case
      const { changed, parentStatusChanged } = updateTaskAndParentStatus(taskId, 'complete')
      if (changed && parentStatusChanged) updateTaskCounts('increment')
    }
  }

  const reopenTaskInUI = (taskId: number) => {
    const task = getTaskById(taskId)
    if (task) {
      // Parent task case
      task.status = 'incomplete'
      updateTaskCounts('decrement')
    } else {
      // Child task case
      const { changed, parentStatusChanged } = updateTaskAndParentStatus(taskId, 'incomplete')
      if (changed && parentStatusChanged) updateTaskCounts('decrement')
    }
  }

  const getTaskById = (id: number): Task | undefined => {
    return taskList.value?.tasks.find((task) => task.id === id)
  }

  const updateTaskCounts = (direction: 'increment' | 'decrement') => {
    const delta = direction === 'increment' ? 1 : -1
    taskList.value.tasks_complete_count += delta
    taskList.value.tasks_incomplete_count -= delta
  }

  const updateTaskAndParentStatus = (
    taskId: number,
    newStatus: 'complete' | 'incomplete',
  ): { changed: boolean; parentStatusChanged: boolean } => {
    const { task, parent } = findTaskAndParent(taskId)
    if (!task) return { changed: false, parentStatusChanged: false }

    const prevStatus = task.status
    task.status = newStatus

    let parentChanged = false

    // Only if the task is a child
    if (parent) {
      const children = parent.children
      const allComplete = children.every((c) => c.status === 'complete')
      const anyIncomplete = children.some((c) => c.status === 'incomplete')

      const parentPrevStatus = parent.status

      if (allComplete) {
        parent.status = 'complete'
      } else if (anyIncomplete) {
        parent.status = 'incomplete'
      }

      parentChanged = parent.status !== parentPrevStatus
    }

    const changed = task.status !== prevStatus || parentChanged
    const parentStatusChanged = !!parentChanged

    return { changed, parentStatusChanged }
  }

  const findTaskAndParent = (taskId: number): { task?: Task; parent?: Task } => {
    for (const task of taskList.value.tasks) {
      if (task.id === taskId) return { task }
      const child = task.children.find((c) => c.id === taskId)
      if (child) return { task: child, parent: task }
    }
    return {}
  }

  const reset = () => {
    taskList.value = undefined
  }
  // helpers

  const isTaskComplete = (task: Task) => task?.status === 'complete'

  const isTaskIncomplete = (task: Task) => task?.status === 'incomplete'

  return {
    taskList,
    setSlug,

    fetchTaskList,
    completeTaskList,
    hideTaskList,
    toggleOnHold,
    reopenTaskList,
    showTaskList,
    completeTask,
    reopenTask,
    reset,

    isLoading,
    setLoading,

    isTaskComplete,
    isTaskIncomplete,
  }
})
